/**
 * nav-hover.js
 * Progressive enhancement for the desktop top navigation (<full-menu>).
 *
 * Adds, on top of the theme's existing CSS hover/focus reveal:
 *   - Hover-intent (~120ms open / ~200ms close) so panels don't flicker when
 *     the cursor sweeps across top-level items.
 *   - aria-haspopup / aria-expanded kept in sync with panel state.
 *   - Keyboard: ArrowDown opens & enters the panel, Up/Down roves the links,
 *     Escape closes and returns focus to the trigger.
 *
 * If this script never runs, the CSS falls back to plain :hover / :focus-within
 * reveal — nothing breaks. Mobile (#mobile-menu) is untouched.
 */
(function () {
  'use strict';

  var OPEN_DELAY = 120;   // ms — hover-intent in
  var CLOSE_DELAY = 200;  // ms — hover-intent out
  var hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function init(root) {
    var menu = root.querySelector('.thb-full-menu');
    if (!menu) return;

    // Direct top-level items that own a panel.
    var items = Array.prototype.filter.call(
      menu.children,
      function (li) {
        return li.classList.contains('menu-item-has-children') &&
               li.querySelector(':scope > .sub-menu');
      }
    );
    if (!items.length) return;

    // Signals to CSS that JS hover-intent is in charge (gates :hover reveal).
    root.classList.add('js-hover-intent');

    var openTimer = null;
    var closeTimer = null;
    var current = null; // currently open <li>

    function panelOf(li) { return li.querySelector(':scope > .sub-menu'); }
    function triggerOf(li) { return li.querySelector(':scope > a'); }

    function open(li) {
      if (current === li) return;
      if (current) close(current);
      li.classList.add('is-open');
      var t = triggerOf(li);
      if (t) t.setAttribute('aria-expanded', 'true');
      current = li;
    }

    function close(li) {
      if (!li) return;
      li.classList.remove('is-open');
      var t = triggerOf(li);
      if (t) t.setAttribute('aria-expanded', 'false');
      if (current === li) current = null;
    }

    function clearTimers() {
      if (openTimer) { clearTimeout(openTimer); openTimer = null; }
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    }

    items.forEach(function (li) {
      var trigger = triggerOf(li);
      var panel = panelOf(li);
      if (!trigger || !panel) return;

      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-expanded', 'false');

      // --- Hover-intent (pointer devices only) ---
      if (hoverCapable) {
        li.addEventListener('mouseenter', function () {
          clearTimers();
          openTimer = setTimeout(function () { open(li); }, OPEN_DELAY);
        });
        li.addEventListener('mouseleave', function () {
          clearTimers();
          closeTimer = setTimeout(function () { close(li); }, CLOSE_DELAY);
        });
      }

      // --- Keyboard / focus: open immediately, no intent delay ---
      li.addEventListener('focusin', function () {
        clearTimers();
        open(li);
      });
      li.addEventListener('focusout', function (e) {
        // Close only when focus actually leaves this item's subtree.
        if (!li.contains(e.relatedTarget)) close(li);
      });

      // Links the keyboard can rove through within this panel (visible only —
      // skips hidden labels like the collapsed mega-menu heading).
      function panelLinks() {
        return Array.prototype.slice
          .call(panel.querySelectorAll('a'))
          .filter(function (a) { return a.offsetParent !== null; });
      }

      trigger.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          open(li);
          var links = panelLinks();
          if (links.length) links[0].focus();
        } else if (e.key === 'Escape') {
          close(li);
        }
      });

      panel.addEventListener('keydown', function (e) {
        var links = panelLinks();
        var idx = links.indexOf(document.activeElement);
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (idx < links.length - 1) links[idx + 1].focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (idx <= 0) { trigger.focus(); }
          else { links[idx - 1].focus(); }
        } else if (e.key === 'Escape') {
          e.preventDefault();
          close(li);
          trigger.focus();
        }
      });
    });

    // Global Escape — close whatever is open and restore focus to its trigger.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && current) {
        var t = triggerOf(current);
        close(current);
        if (t) t.focus();
      }
    });
  }

  function boot() {
    document.querySelectorAll('full-menu').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
